import type { InterviewAnswers, WizardStep } from "./wizard-types"

import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { STATUS_TONES } from "@/components/ui/tone-classes"

import { SITE_AREAS } from "../../types/use-case.types"
import { MultiSelectDropdown } from "../fact-sheet/multi-select-dropdown"

type AiInterviewStepProps = {
  step: WizardStep
  answers: InterviewAnswers
  onAnswer: (field: keyof InterviewAnswers, value: string | string[]) => void
}

const SITE_OPTIONS = SITE_AREAS.map((s) => ({ value: s, label: s }))

const STEPS: Partial<
  Record<
    string,
    {
      question: string
      description: string
      field: keyof InterviewAnswers
      placeholder: string
      optional?: boolean
    }
  >
> = {
  "interview-1": {
    question: "What problem are you trying to solve?",
    description: "Describe the business pain or bottleneck in 1-2 sentences.",
    field: "problem",
    placeholder:
      "e.g. Our team spends 10+ hours per week manually compiling reports from three different systems...",
  },
  "interview-2": {
    question: "Who are the users affected?",
    description: "Describe who will benefit and select relevant sites.",
    field: "users",
    placeholder: "e.g. 15 analysts in the finance department across three Nordic sites...",
  },
  "interview-3": {
    question: "What does the ideal solution look like?",
    description: "Describe what success looks like — how AI or automation would help.",
    field: "solution",
    placeholder:
      "e.g. An automated pipeline that pulls data from ERP and CRM, generates dashboards overnight...",
  },
  "interview-4": {
    question: "Any data sources or systems involved?",
    description: "List relevant data sources, APIs, or systems. This step is optional.",
    field: "dataSources",
    placeholder: "e.g. SAP ERP exports, SharePoint document library, production database...",
    optional: true,
  },
}

export function AiInterviewStep({ step, answers, onAnswer }: AiInterviewStepProps) {
  const config = STEPS[step]
  if (!config) return null

  const stepNum = parseInt(step.split("-")[1])
  const value = answers[config.field]

  return (
    <div className="space-y-4">
      <div>
        <div className="text-muted-foreground mb-1 text-xs font-medium">
          Question {stepNum} of 4{config.optional ? " (optional)" : ""}
        </div>
        <h3 className="text-lg font-semibold">{config.question}</h3>
        <p className="text-muted-foreground mt-1 text-sm">{config.description}</p>
      </div>

      <Textarea
        className="min-h-[100px] resize-none"
        placeholder={config.placeholder}
        value={value as string}
        onChange={(e) => onAnswer(config.field, e.target.value)}
      />

      {step === "interview-2" && (
        <div className="space-y-2">
          <Label className="text-sm">Sites / Areas</Label>
          <MultiSelectDropdown
            options={SITE_OPTIONS}
            placeholder="Select relevant sites..."
            selected={answers.sites}
            tagClassName={STATUS_TONES.info.solid}
            onChange={(selected) => onAnswer("sites", selected)}
          />
        </div>
      )}

      {/* Progress bar */}
      <div className="flex gap-1.5">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${i <= stepNum ? "bg-primary-ink" : "bg-muted"}`}
          />
        ))}
      </div>
    </div>
  )
}
